/*
  Injectable units — placement of renderer-authored, self-styled embedded units.

  This is the generic renderer-authored injectable-unit placement path. It
  places `yahoo_cta`, `bing_cta`, `yahoo_ads`, and `dynamic_creative` units: the
  @serp/renderer authors the full iframe document (markup + scoped CSS + the
  resize/click postMessage bootstrap) and @rampjs/server returns them as
  `server.units`. This module's job is placement + instrumentation only: drop
  each unit's `html` into an iframe srcdoc, append it to the publisher's
  container, fire impression beacons, and relay click/resize messages from the
  iframe. The markup builders that used to live here moved to the renderer.

  Historical note: this module was formerly `yahoo-cta.ts` (Yahoo CTA was the
  first such unit). It is no longer Yahoo-CTA-specific. Some string values and
  page-global keys below deliberately RETAIN the `yahoo-cta` name — see the
  deploy-skew comments at each; do not "finish" the rename by changing those
  values or the resize/click relay silently breaks during a rollout.
*/

import { CONSOLE_CAPTURE_PREFIX } from "@ascent/debugger";
import { appendPartnerCookies, sendPingback } from "@ascent/papi-core/client";
import type { Response } from "@rampjs/server";
import { rampJsUnitLoaded } from "./events";
import {
  TrackingEvent,
  observeAdContainer,
  observeInView,
  withEventMarker,
} from "./tracking-events";

// Listener registration is page-global; a flag prevents duplicate handlers
// when multiple ramp.js instances coexist on the same page.
//
// ⚠️ These `_yahoo*` keys are a cross-instance runtime contract: independently
// deployed ramp.js bundles of different versions can coexist on one page, and
// these page-global keys are what dedup their listeners/head-injection. Renaming
// the keys mid-rollout defeats the dedup (two bundles would each think they're
// first). Keep the key strings exactly as-is even though the module is no longer
// Yahoo-specific.
declare global {
  interface Window {
    _yahooUnitsHeadApplied?: boolean;
    _yahooCtaListenerInstalled?: boolean;
    _yahooCtaIframes?: Set<HTMLIFrameElement>;
    _rampjsCspReports?: Set<string>;
  }
}

/** Distinct unit CSP reports beaconed per page. */
const MAX_CSP_REPORTS = 20;

/**
 * Iframes we've injected, keyed by reference. Used to verify
 * `postMessage` event.source against trusted windows and to look up the
 * resize target without needing a CSS attribute selector.
 */
function getIframeRegistry(): Set<HTMLIFrameElement> {
  if (!window._yahooCtaIframes) window._yahooCtaIframes = new Set();
  return window._yahooCtaIframes;
}

// ⚠️ Cross-package wire contract — keep the VALUE "rampjs.yahoo-cta".
// Must match @serp/renderer UNIT_MESSAGE_SOURCE
// (@serp/renderer/src/fragments/document.ts): the renderer authors the
// in-iframe bootstrap that posts resize/click messages tagged with this
// source, and this client checks it in the message listener. The renderer and
// client deploy INDEPENDENTLY, so a rollout always has a window where an old
// iframe talks to a new client (or vice-versa). Renaming this identifier is
// fine, but changing the string value would break resize + click relay during
// that window unless both sides migrate together (dual-read). Both sides carry
// "must match the other package" comments — keep them in sync.
const RESIZE_MESSAGE_SOURCE = "rampjs.yahoo-cta";

// QA / publisher-preview override param. The server reads it from pageUrl and
// resolves the CTA stylesheet accordingly; kept here so the debug panel can
// surface the available ids and set the param. No longer used for client-side
// CSS resolution (the renderer/server own that now).
export const STYLE_ID_OVERRIDE_PARAM = "rampjs_cta_style_id";

/**
 * Known stylesheet ids shipped under packages/@rampjs/client/styles/.
 * Kept in lockstep with the deploy upload set so the debug panel can surface
 * them as override options. New CSS files added to that folder should also be
 * appended here.
 */
export const KNOWN_INJECTABLE_UNIT_STYLE_IDS: string[] = [
  "default",
  "veve_default",
  "9267",
  "1326228123",
  "2040472930",
  "6985574355",
  "7869764001",
  "7869764001_wrap",
  "8226227397",
  "9403857478",
  "autocta1",
  "autocta2",
  "autocta3",
  "autocta4",
  "autocta5",
  "srcfinder",
];

/**
 * Apply the renderer-authored page-level head injection (`server.unitsHead`)
 * to the host document, once per page. This carries Yahoo's page-level
 * instrumentation — the selectTier loader script and the geo-beacon pixel —
 * which the renderer now owns (see @serp/renderer select-tier helper). The
 * client no longer builds this; it just appends the authored tags.
 *
 * Runs independently of unit placement: the head is page-level and shared
 * across any Yahoo Ads or Yahoo CTA units on the page. A page-global flag
 * guards against a double-apply when multiple ramp.js instances coexist.
 */
export function applyUnitsHead(server: Response): void {
  if (window._yahooUnitsHeadApplied) return;

  const head = server?.unitsHead;
  if (!head) return;

  const scripts = head.scripts;
  if (Array.isArray(scripts)) {
    for (const script of scripts) {
      const el = document.createElement("script");
      if (script.attributes) {
        for (const [name, value] of Object.entries(script.attributes)) {
          if (value === false) continue;
          el.setAttribute(name, value === true ? "" : value);
        }
      }
      if (typeof script.src === "string" && script.src) {
        el.src = script.src;
      } else if (typeof script.content === "string") {
        el.text = script.content;
      }
      document.head.appendChild(el);
    }
  }

  const styles = head.styles;
  if (Array.isArray(styles)) {
    for (const style of styles) {
      if (typeof style.href === "string" && style.href) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = style.href;
        document.head.appendChild(link);
      } else if (typeof style.content === "string") {
        const el = document.createElement("style");
        el.textContent = style.content;
        document.head.appendChild(el);
      }
    }
  }

  window._yahooUnitsHeadApplied = true;
}

// Renderer-authored unit types this module places. Yahoo CTA, Yahoo ads, and
// the dynamic_creative container all flow through the same iframe srcdoc
// placement path — the renderer owns the markup difference (each authors its
// own document + scoped CSS + the shared resize/click bootstrap); the client
// treats them identically here. dynamic_creative (EPIC-977) is a user-pasted
// HTML/JS creative blob the API feeds through @rampjs/server.units exactly like
// the Yahoo units.
const PLACEABLE_UNIT_TYPES = new Set([
  "yahoo_cta",
  // Bing CTA (ABE-1025): the Bing cta_keyword widget. Same markup + styles as
  // yahoo_cta; placed identically.
  "bing_cta",
  "yahoo_ads",
  "dynamic_creative",
  // Embedded SERP (ABE-789): a renderer-authored partial-SERP iframe. The
  // client places it identically to the ad units — the renderer owns the
  // document (markup + scoped CSS + resize/click bootstrap), so this is just
  // another placeable type here. Inline (host-DOM) placement is future work.
  "embedded_serp",
  // Search bar (ABE-903): a standalone search-bar unit, deliberately NOT part
  // of any ad component. Sharing a container with the Yahoo ads unit makes it
  // its own row below them — containers are appended to in response order, so
  // placement needs nothing here beyond membership in this set. Its impression
  // fires on insert like any unit; its click beacon fires on form submit,
  // relayed by the unit bootstrap.
  "search_bar",
  // Host-DOM SERP pieces (Bing ads, algo, shopping, enrichment). The renderer
  // sets placement: "inline" and `html` is the component markup, not an iframe
  // document. Membership here is what lets onResponse see them.
  "ads_bing",
  "product_ads_bing",
  "web_results",
  "web_brave",
  "shopping_bing",
  "shopping_pole_bing",
  "extended_data_bing",
  "enrichment_slot",
  // Locotomato internet_equity widget (ABE-993). The one INLINE unit: the
  // renderer marks it `placement: "inline"` and it goes into the host DOM, not
  // an iframe (see placeInlineUnit).
  "internet_equity",
]);

// iframe `title` per unit type, for accessibility. Falls back to a generic
// label for any future type that isn't mapped here.
const UNIT_IFRAME_TITLE: Record<string, string> = {
  yahoo_cta: "Related searches",
  bing_cta: "Related searches",
  yahoo_ads: "Sponsored results",
  dynamic_creative: "Sponsored content",
  embedded_serp: "Search results",
  search_bar: "Search",
};

// `adType` reported on the rampJsUnitLoaded event per unit type, so listeners
// (and the debug panel) can distinguish a dynamic_creative load from a Yahoo
// one. Falls back to "yahoo" for the Yahoo units this module originally placed.
const UNIT_LOADED_AD_TYPE: Record<string, string> = {
  yahoo_cta: "yahoo",
  yahoo_ads: "yahoo",
  bing_cta: "bing",
  dynamic_creative: "dynamic_creative",
  embedded_serp: "embedded_serp",
  // Not an ad — listeners keying off "yahoo" must not count a search bar as an
  // ad load, so it reports its own type.
  search_bar: "search_bar",
  ads_bing: "bing",
  product_ads_bing: "bing",
  web_results: "web",
  web_brave: "web",
  shopping_bing: "bing_shopping",
  shopping_pole_bing: "bing_shopping",
  extended_data_bing: "bing_shopping",
  enrichment_slot: "enrichment",
  internet_equity: "internet_equity",
};

/**
 * Place renderer-authored unit fragments (Yahoo CTA + Yahoo ads +
 * dynamic_creative + embedded_serp + search_bar, and the inline
 * internet_equity widget) into their publisher containers.
 *
 * The renderer already produced a complete iframe document per unit (markup +
 * scoped CSS + bootstrap) and the server attached them as `server.units`. Here
 * we only drop each into an iframe, fire impression beacons, and let the parent
 * message listener handle resize + click relay. Units marked
 * `placement: "inline"` skip the iframe and go into the container directly
 * (placeInlineUnit).
 */
export function onResponse(server: Response): void {
  const units = server?.units;
  if (!Array.isArray(units) || units.length === 0) return;

  installParentMessageListener();

  for (const unit of units) {
    if (!PLACEABLE_UNIT_TYPES.has(unit.type)) continue;

    const containerName = unit.containerName;
    if (typeof containerName !== "string" || !containerName) {
      if (__DEBUG__) {
        console.warn(
          `${CONSOLE_CAPTURE_PREFIX} Injectable unit missing containerName; skipping. type='${unit.type}' id='${unit.id}'`,
        );
      }
      continue;
    }

    const container = document.getElementById(containerName);
    if (!container) {
      if (__DEBUG__) {
        console.warn(
          `${CONSOLE_CAPTURE_PREFIX} Injectable unit container not found: '${containerName}' (type='${unit.type}')`,
        );
      }
      continue;
    }

    if (unit.placement === "inline") {
      const wrapper = placeInlineUnit(unit, container, containerName);
      instrumentUnit(unit, wrapper, containerName);
      continue;
    }

    const iframe = document.createElement("iframe");
    iframe.title = UNIT_IFRAME_TITLE[unit.type] ?? "Sponsored content";
    // ABE-856: the renderer resolves the sandbox policy per unit (only
    // dynamic_creative today; see @serp/renderer fragments/sandbox.ts) and
    // stamps it on the unit. Apply it BEFORE assigning srcdoc so the sandbox is
    // in force when the document loads. Absent `sandbox` = no attribute (the
    // current behavior for all other unit types). An old client that predates
    // this field simply never sandboxes — a safe deploy-skew degradation.
    if (typeof unit.sandbox === "string" && unit.sandbox) {
      iframe.setAttribute("sandbox", unit.sandbox);
    }
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("scrolling", "no");
    iframe.style.border = "0";
    iframe.style.display = "block";
    iframe.style.width = "100%";
    iframe.style.height = "0px";
    iframe.dataset.injectableUnitRegion = containerName;
    iframe.dataset.injectableUnitType = unit.type;
    iframe.srcdoc = unit.html;

    container.appendChild(iframe);
    getIframeRegistry().add(iframe);

    instrumentUnit(unit, iframe, containerName);
  }
}

type Unit = NonNullable<Response["units"]>[number];

/**
 * Impression + in_view beacons and the rampJsUnitLoaded event for a placed
 * unit. `element` is the unit's own node in the host DOM (its iframe, or the
 * inline wrapper).
 */
function instrumentUnit(
  unit: Unit,
  element: Element,
  containerName: string,
): void {
  // Fire impression beacons after the unit is in the DOM. Uses the same
  // pingback transport (sendBeacon → fetch keepalive) the Google RSoC path
  // uses, so partner-cookie behavior matches.
  for (const beaconUrl of unit.impressionBeaconUrls) {
    sendBeaconUrl(beaconUrl);
  }

  // Standardized in_view signal (ABE-864): fire once, only if the slot
  // actually scrolls into the viewport (may never fire). Impressions above
  // stay insert-fired and unchanged; this is an additive signal, reusing the
  // impression-beacon URL with an `event=in_view` marker so the backend can
  // split the metric without a separate server-supplied view URL. We observe
  // the unit's own element (iframe or inline wrapper) — it lives in the parent
  // DOM, so no renderer / in-iframe change is needed. Disconnects are dropped intentionally: the
  // observer self-disconnects on first intersection, and any never-seen units
  // are cleaned up when the page is torn down.
  const impressionBeaconUrls = unit.impressionBeaconUrls;
  if (impressionBeaconUrls.length > 0) {
    observeInView(element, () => {
      for (const beaconUrl of impressionBeaconUrls) {
        sendBeaconUrl(withEventMarker(beaconUrl, TrackingEvent.InView));
      }
    });
  }

  window.dispatchEvent(
    rampJsUnitLoaded(
      containerName,
      UNIT_LOADED_AD_TYPE[unit.type] ?? unit.type,
    ),
  );
}

/**
 * Place an inline unit (internet_equity, ABE-993) directly in the publisher
 * container, inside a wrapper div so its observers watch just this unit and not
 * whatever else shares the container.
 *
 * Scripts inserted via innerHTML never execute, so each `<script>` in the
 * renderer's markup is recreated with createElement + setAttribute (as
 * applyUnitsHead does) after the wrapper is in the document. Carousel scripts
 * look up their arrows with getElementById, which only works once the nodes
 * are attached. The renderer escaped the attribute values; the browser's
 * parser decodes them back here.
 *
 * Also wires `loadedCallbackUrl` into the shared container observers, which
 * fire `mutation_observed` (the widget injected an iframe) and
 * `resize_observed` (the widget sized itself), using the CSA wire shape
 * (`<event>=true` plus `container=<id>`, with partner cookies appended).
 */
function placeInlineUnit(
  unit: Unit,
  container: HTMLElement,
  containerName: string,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.dataset.injectableUnitRegion = containerName;

  const template = document.createElement("template");
  template.innerHTML = unit.html;
  wrapper.appendChild(template.content);
  container.appendChild(wrapper);

  for (const inert of Array.from(wrapper.querySelectorAll("script"))) {
    const script = document.createElement("script");
    for (const { name, value } of Array.from(inert.attributes)) {
      script.setAttribute(name, value);
    }
    script.text = inert.text;
    inert.replaceWith(script);
  }

  // The observer script scans once when it starts, and a later copy no-ops
  // because that scan already exists. Cards inserted after that first pass
  // still need a scan. Nodes already watching are skipped.
  scanInlineImpressionBeacons();

  const loadedCallbackUrl = unit.loadedCallbackUrl;
  if (typeof loadedCallbackUrl === "string" && loadedCallbackUrl) {
    observeAdContainer(wrapper, (event) => {
      try {
        const url = new URL(loadedCallbackUrl);
        url.searchParams.set(event, "true");
        url.searchParams.set("container", containerName);
        appendPartnerCookies(url);
        sendPingback(url);
      } catch (error) {
        if (__DEBUG__) {
          console.debug(
            `${CONSOLE_CAPTURE_PREFIX} Failed to send ${unit.type} ${event} pingback:`,
            error,
          );
        }
      }
    });
  }

  return wrapper;
}

function scanInlineImpressionBeacons(): void {
  const scan =
    window.__RAMPJS_EE_IMPRESSION__?.scan ?? window.__rampjsEeScanBeacons;
  if (typeof scan !== "function") return;
  try {
    scan();
  } catch (error) {
    if (__DEBUG__) {
      console.debug(
        `${CONSOLE_CAPTURE_PREFIX} Inline impression scan failed:`,
        error,
      );
    }
  }
}

function sendBeaconUrl(value: string): void {
  try {
    sendPingback(new URL(value));
  } catch {
    sendPingback(value);
  }
}

/**
 * Push the host viewport width into one injected iframe so its in-document
 * bootstrap can set `data-parent-width` (mobile/desktop). Needed for SANDBOXED
 * units (ABE-856): an opaque-origin frame can't read `window.parent.innerWidth`
 * itself. Harmless for unsandboxed units, which also read the parent directly.
 */
function pushParentWidth(iframe: HTMLIFrameElement): void {
  iframe.contentWindow?.postMessage(
    { source: RESIZE_MESSAGE_SOURCE, parentWidth: window.innerWidth },
    "*",
  );
}

function installParentMessageListener(): void {
  if (window._yahooCtaListenerInstalled) return;
  window._yahooCtaListenerInstalled = true;

  window.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as
      | {
          source?: string;
          region?: string;
          height?: number;
          beacon?: string;
          requestParentWidth?: boolean;
          cspViolation?: {
            effectiveDirective?: string;
            blockedURI?: string;
            disposition?: string;
          };
        }
      | undefined;
    if (!data || data.source !== RESIZE_MESSAGE_SOURCE) return;

    // Only honor messages whose event.source is one of the iframes we
    // injected — otherwise any script on the host page could spoof a
    // beacon payload and trigger arbitrary requests. Works for sandboxed
    // (opaque-origin) frames too: this checks source identity, not origin.
    const sourceIframe = findIframeForSource(event.source);
    if (!sourceIframe) return;

    // ABE-856: a sandboxed unit asks the parent to push the viewport width
    // (it can't read window.parent.innerWidth across an opaque origin).
    if (data.requestParentWidth) {
      pushParentWidth(sourceIframe);
      return;
    }

    // Srcdoc meta policies cannot use report-uri. The unit relays the
    // violation here; we beacon it to the uncached rampjs origin. Region and
    // unit type come from the iframe we placed, not the message, and each
    // page sends at most MAX_CSP_REPORTS distinct reports.
    if (data.cspViolation && typeof data.cspViolation === "object") {
      const field = (value: unknown) =>
        typeof value === "string" ? value.slice(0, 256) : undefined;
      const report = {
        effectiveDirective: field(data.cspViolation.effectiveDirective),
        blockedURI: field(data.cspViolation.blockedURI),
        disposition: field(data.cspViolation.disposition),
        region: sourceIframe.dataset.injectableUnitRegion,
        unitType: sourceIframe.dataset.injectableUnitType,
      };
      const body = JSON.stringify(report);
      const sent = (window._rampjsCspReports ??= new Set());
      if (sent.has(body) || sent.size >= MAX_CSP_REPORTS) return;
      sent.add(body);
      try {
        const endpoint = new URL("/csp-report", RAMPJS_SERVER_URL);
        navigator.sendBeacon(endpoint.toString(), body);
      } catch {
        // RAMPJS_SERVER_URL is a compile-time define and is absent in some hosts.
      }
      return;
    }

    if (typeof data.beacon === "string" && data.beacon) {
      sendBeaconUrl(data.beacon);
      return;
    }

    if (typeof data.height === "number" && data.height > 0) {
      sourceIframe.style.height = `${Math.ceil(data.height)}px`;
    }
  });

  // Re-push the width to every injected iframe on resize so a sandboxed unit's
  // data-parent-width bucket tracks the viewport (unsandboxed units also
  // recompute from their own direct read; the push is a harmless no-op there).
  window.addEventListener("resize", () => {
    for (const iframe of getIframeRegistry()) pushParentWidth(iframe);
  });
}

function findIframeForSource(
  source: MessageEventSource | null,
): HTMLIFrameElement | null {
  if (!source) return null;
  for (const iframe of getIframeRegistry()) {
    if (iframe.contentWindow === source) return iframe;
  }
  return null;
}
